import React, { useRef, useState } from 'react';
import {
	AutoSuggestInput,
	IAutoSuggestInputProps,
	IDropdownItem,
	StyledTextFieldErrorMessage,
} from '../../../../ui';
import { Controller, useFormContext } from 'react-hook-form';
import { StateChangeOptions } from 'downshift';
import { getValueForItem } from './helpers/getValueForItem';
import { useSelectItemOnBlur } from './helpers/useSelectItemOnBlur';
import {
	getFilteredItemsForValue,
	ItemFilter,
} from './helpers/getFilteredItemsForValue';
import { capitalize } from '../../utils/capitalize';

export interface IControlledAutoSuggestProps<
	TItemType extends IDropdownItem = IDropdownItem,
> extends IAutoSuggestInputProps<TItemType> {
	name: string;
	filter?: ItemFilter<TItemType>;
}

export const ControlledAutoSuggest = <TItemType extends IDropdownItem>({
	name,
	...props
}: IControlledAutoSuggestProps<TItemType>) => {
	const {
		control,
		formState: { errors },
	} = useFormContext();

	const [hasBlurred, setHasBlurred] = useState(false);
	const invalid = !!errors[name] || hasBlurred;
	const errorMessageId = `${name}-error`;

	return (
		<>
			<Controller
				name={name}
				control={control}
				render={({ field }) => (
					<InnerAutoSuggestInput
						{...props}
						value={field.value}
						onChange={field.onChange}
						setHasBlurred={setHasBlurred}
						invalid={invalid}
						ariaDescribedBy={errorMessageId}
					/>
				)}
			/>
			{invalid ? (
				<StyledTextFieldErrorMessage role="alert" id={errorMessageId}>
					{`${capitalize(name)} is required`}
				</StyledTextFieldErrorMessage>
			) : null}
		</>
	);
};

interface IInnerAutoSuggestProps<
	TItemType extends IDropdownItem = IDropdownItem,
> extends Omit<IAutoSuggestInputProps<TItemType>, 'onChange'> {
	value?: string;
	onChange: (value: string) => void;
	filter?: ItemFilter<TItemType>;
	setHasBlurred: (blurred: boolean) => void;
}

const InnerAutoSuggestInput = <TItemType extends IDropdownItem>({
	items,
	value = '',
	onChange,
	setHasBlurred,
	filter,
	...props
}: IInnerAutoSuggestProps<TItemType>) => {
	const filteredItems = getFilteredItemsForValue(items, filter, value);
	const selectItemOnBlur = useSelectItemOnBlur(filteredItems);

	const autoSuggestInputRef = useRef<HTMLDivElement>(null);

	const handleFocusLeave = () => {
		const focussed = autoSuggestInputRef.current?.focus();

		if (!focussed && value.trim() === '') {
			setHasBlurred(true);
		}
	};

	const handleChange = (state: StateChangeOptions<TItemType>) => {
		if ('selectedItem' in state) {
			onChange(getValueForItem(state.selectedItem) ?? '');
			setHasBlurred(false);
		} else if ('inputValue' in state) {
			onChange(state.inputValue ?? '');
		}
	};

	return (
		<div ref={autoSuggestInputRef} onBlur={handleFocusLeave}>
			<AutoSuggestInput
				{...props}
				onStateChange={handleChange}
				stateReducer={selectItemOnBlur}
				inputValue={value}
				items={filteredItems}
				defaultHighlightedIndex={0}
			/>
		</div>
	);
};
